import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Link } from "wouter";
import { ArrowLeft, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  {
    title: "Academic Clinical Fellowship (ACF)",
    content: `ACFs are available at ST3–4 level across most training regions. They offer 25% protected research time (approximately 1 day per week over 3 years) during clinical training, allowing you to develop research skills, apply for grants, and build an academic track record.

Competitive ACFs are advertised through Health Education England / NHS England nationally in the autumn recruitment round. You will need a strong research plan, a named supervisor, and a clear narrative about why academic rheumatology is the right path.

During an ACF, you should aim to complete a higher degree (PhD or MD), publish at least one first-author paper, present at BSR and EULAR, and apply for an NIHR Academic Clinical Lectureship (ACL) or equivalent post-CCT fellowship.`,
  },
  {
    title: "Academic Clinical Lectureship (ACL)",
    content: `ACLs are competitive post-CCT or near-CCT posts typically offering 50% research / 50% clinical. They are usually 4 years in duration and are funded by NIHR in partnership with universities. ACLs are designed to develop independent research leaders.

To be competitive for an ACL, you should have a PhD (or be close to completion), at least 2-3 first-author publications in peer-reviewed journals, a clear independent research programme, and external grant funding (MRC, Wellcome, NIHR, or ARUK).

After an ACL, the natural progression is to a Senior Lecturer / Reader or Clinical Senior Lecturer post, with a pathway to Professor of Rheumatology.`,
  },
  {
    title: "PhD and MD Training",
    content: `A PhD (typically 3 years full-time) or MD (usually 2 years full-time or 3-4 years part-time) is the core qualification for an academic career. Options include:

• Wellcome Trust Clinical PhD Programme — highly competitive, generous stipend, broad research training
• NIHR Pre-doctoral Fellowship — typically 2 years, 75% research protected time
• MRC Doctoral Training Partnership — competitive national DTP at major research universities
• BSR/ARUK Research Grants — for smaller projects within an academic attachment
• EULAR Research Bursaries — funding for European collaborative projects

The topic should ideally align with the department's research strengths and your supervisor's grant portfolio. A translational focus (bench-to-bedside in autoimmunity, imaging biomarkers, biologic response prediction) tends to have good funding prospects.`,
  },
  {
    title: "Balancing Research and Clinical Training",
    content: `Academic training requires careful planning of your ARCP portfolio alongside research output. Key points:

• Ensure your clinical competencies are documented in your ePortfolio even in ACF years — Educational Supervisors may underestimate the time burden of maintaining clinical training alongside research
• Plan your research timeline: ethics applications, data collection, and writing take longer than expected
• Use your SPA time in consultant posts to maintain academic output post-CCT
• The Walport Integrated Clinical Academic Training pathway is designed to bridge exactly this gap — familiarise yourself with it early

Network through BSR Trainee Network, EULAR Young EULAR, and attend at least one international meeting (BSR, EULAR, ACR) per year from ST5+ onwards.`,
  },
];

export default function AcademicTraining() {
  return (
    <DoctorLayout title="Academic Training">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link href="/doctor/career">
            <Button variant="ghost" size="sm" className="gap-1 text-slate-500">
              <ArrowLeft className="h-4 w-4" />
              Career Pathways
            </Button>
          </Link>
        </div>

        <div className="bg-gradient-to-br from-blue-800 to-blue-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <FlaskConical className="h-8 w-8 text-blue-300" />
            <div>
              <h2 className="text-xl font-bold">Academic Training in Rheumatology</h2>
              <p className="text-blue-300 text-sm">ACF, CL, PhD/MD — the Walport integrated pathway</p>
            </div>
          </div>
          <p className="text-blue-200 text-sm leading-relaxed">
            Academic rheumatology is a rewarding and competitive pathway. UK training offers structured integrated academic clinical training from ACF through to independent research chair. Planning early, building the right networks, and choosing your supervisor carefully are the most important early career decisions.
          </p>
        </div>

        <div className="space-y-4">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3">
              <h3 className="text-lg font-bold text-slate-900">{section.title}</h3>
              <div className="space-y-3">
                {section.content.split("\n\n").map((para, i) => (
                  <p key={i} className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{para}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 space-y-2">
          <h4 className="font-bold text-slate-900">Key Links</h4>
          <ul className="space-y-1 text-sm">
            <li><a href="https://www.nihr.ac.uk/explore-nihr/support/clinical-research-network.htm" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">NIHR Academic Clinical Fellowships & Lectureships</a></li>
            <li><a href="https://wellcome.org/grant-funding/schemes/clinical-phd-programmes" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">Wellcome Trust Clinical PhD Programmes</a></li>
            <li><a href="https://www.versusarthritis.org/research/apply-for-funding/" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">Versus Arthritis (ARUK) Research Grants</a></li>
            <li><a href="https://www.eular.org/edu_eular_research_bursaries.cfm" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">EULAR Research Bursaries</a></li>
          </ul>
        </div>
      </div>
    </DoctorLayout>
  );
}
